/*
  The grade panel: the portal's grade tile (card-grading-admin-ui ViewGradingScore) above
  the four sub-grade arcs.

  The tile is gold above 9 and silver at 9 and below and for Authentic, with black type in
  both, as the portal's is. Those two fills are the slab's own colours rather than theme
  surfaces, so they do not flip with light/dark. The gold stops are --pcg-gold-chrome (brand
  gold in both modes) down to --pcg-gold-dark, which keeps black type above 4.5:1 at the
  bottom of the gradient in light mode, where --pcg-gold-deep would not.
*/

import { isAuthentic, gradeFigure, isGoldGrade, ReportRow, selectSubGrades } from 'lib/gradeReport';

import ScoreArc from './ScoreArc';

/*
  Boxed like the label's own grade area (Robert, 2026-10-21: "a little bit more box like
  like our label"): a flat block of the accent with square corners, grade name over the
  big figure, the way the engraved label stacks them. Its shape is the label's too: the
  grade area is 70 x 104 on the 385 x 116 label, so the tile is that portrait 70:104. The gradient is kept faint so the
  tile still reads as metal rather than a flat sticker.

  It is 100px wide, down from 118, and the arcs beside it grew, so the two read at one scale
  rather than the tile towering over the row (Robert, 2026-10-21: "make it a little smaller …
  make sub grades bigger"). That leaves about 88px for the name, which "Pristine" fits at
  18px but "Gem Mint 10" (102px there) and "Near Mint+" do not, so a name over nine letters
  drops to 15px instead of running past the tile's edge.
*/
const longName = (name: string): boolean => name.length > 9;

const GOLD_TILE = {
  background: 'linear-gradient(to bottom, rgb(var(--pcg-gold-chrome)), rgb(var(--pcg-gold-chrome)) 55%, rgb(var(--pcg-gold-dark)))',
};

/* The ONE silver (batch 4, D8): the label preview and the arcs below 9.5 take it too. */
const SILVER_TILE = {
  background: 'linear-gradient(to bottom, rgb(var(--pcg-silver-hi)), rgb(var(--pcg-silver-hi)) 55%, rgb(var(--pcg-silver-lo)))',
};

function GradeTile({ row }: { row: ReportRow }) {
  const authentic = isAuthentic(row);
  const figure = gradeFigure(row);
  const name = authentic ? 'Authentic' : row.grade;
  /* An Authentic card has no sub-grades to show: its four zeros say nothing. */
  const subGrades = authentic ? [] : selectSubGrades(row);

  /*
    From xl the tile sits beside the arcs rather than above them, in half the height, which
    is most of what lets the report fit one screen (D2). There it is drawn at the RIGHT end
    of the row, after the four arcs, as Robert asked (2026-10-21): `xl:order-last` moves it
    visually only. The DOM keeps the tile first, so a screen reader still announces the
    final grade before the sub-grades, and below xl, where the two stack, it stays on top
    as the first thing a phone shows.
  */
  return (
    <div className="flex flex-col items-center gap-3 xl:flex-row xl:items-stretch xl:justify-center xl:gap-6">
      <div
        className="flex aspect-[70/104] w-[100px] shrink-0 flex-col items-center justify-center gap-0.5 rounded-[3px] px-1.5 text-center text-black xl:order-last"
        style={isGoldGrade(row) ? GOLD_TILE : SILVER_TILE}
      >
        {/*
          One reading for the pair, "10, Gem Mint", under the section's "Grade" heading. The
          two lines are styled apart but read together.
        */}
        <p className="sr-only">{authentic ? 'Authentic' : `${figure}, ${name}`}</p>
        <div
          aria-hidden="true"
          className={`whitespace-nowrap font-display font-bold leading-tight ${longName(name) ? 'text-[15px]' : 'text-lg'}`}
        >
          {name}
        </div>
        <div aria-hidden="true" className="font-display text-5xl font-extrabold leading-none tabular-nums">
          {figure}
        </div>
      </div>

      {subGrades.length > 0 && (
        <ul className="m-0 flex w-full list-none flex-wrap items-center justify-center gap-x-2 gap-y-2 sm:gap-x-3 p-0 sm:justify-around xl:w-auto xl:flex-1 xl:flex-nowrap">
          {subGrades.map((sub) => (
            <li key={sub.title}>
              <ScoreArc title={sub.title} value={sub.value} />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default GradeTile;
